import type { CatalogImage } from '@/features/catalog-image/types';
import type { Checkout } from '@/features/booking/types';
import type { ProductVariant, CreateVariantInput } from '@/features/product-variant/types';
import type { PetSpecies } from '@/features/pet/types';

/** A single line's catalog. A cart (and order) MAY span multiple vendors AND mix FOOD +
 *  PHARMACY; the order-level vertical is null when mixed. */
export type CommerceVertical = 'FOOD' | 'PHARMACY';

/**
 * Shared order/shipment status enum — mirrors the backend `OrderStatus` used by
 * both CommerceOrder.status and CommerceShipment.status. PENDING_PAYMENT only
 * ever appears at the order level, before shipments become visible to vendors;
 * REJECTED/ACCEPTED/OUT_FOR_DELIVERY/READY only ever appear at the shipment level.
 */
export type CommerceStatus =
  | 'PENDING_PAYMENT'
  | 'PLACED'
  | 'ACCEPTED'
  | 'OUT_FOR_DELIVERY'
  | 'READY'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'REJECTED';

export type CommercePayment = { status: string; amount_inr: string; is_dummy: boolean; order_ref: string | null };

export type CommerceOrderLine = {
  id: string;
  product_id: string | null;
  variant_id: string | null;
  variant_label: string | null;
  name: string;
  unit_price_inr: string;
  quantity: number;
  line_total_inr: string;
  is_prescription_required: boolean;
};

/** A shipment nested inside a CommerceOrder — the owner-facing per-vendor slice of an order. */
export type CommerceOrderShipment = {
  id: string;
  vertical: CommerceVertical; // single-vendor → single-vertical
  vendor_profile_id: string;
  vendor_business_name: string | null;
  status: CommerceStatus;
  subtotal_inr: string;
  tracking_ref: string | null;
  tracking_url: string | null;
  cancelled_by: string | null;
  cancel_reason: string | null;
  lines: CommerceOrderLine[];
};

export type CommerceOrder = {
  id: string;
  user_id: string;
  vertical: CommerceVertical | null; // null = a mixed FOOD+PHARMACY order
  status: CommerceStatus;
  rollup_status: CommerceStatus;
  goods_total_inr: string;
  delivery_fee_inr: string;
  grand_total_inr: string;
  contact_phone: string;
  address: string | null;
  landmark: string | null;
  city: string | null;
  pincode: string | null;
  notes: string | null;
  prescription_required: boolean;
  prescriptions: CatalogImage[];
  payment: CommercePayment | null;
  cancelled_by: string | null;
  cancel_reason: string | null;
  created_at: string;
  updated_at: string;
  shipments: CommerceOrderShipment[];
};

/** Place-order response also carries the Razorpay checkout descriptor (null for the dummy gateway). */
export type PlaceCommerceOrderResponse = CommerceOrder & { checkout: Checkout | null };

/** Razorpay client-callback payload posted to /pay/verify. */
export type VerifyCommercePaymentInput = {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
};

/** Vendor shipment status intents (path segments) — keep in sync with the backend controller. */
export type CommerceShipmentIntent = 'accept' | 'reject' | 'out-for-delivery' | 'ready' | 'complete' | 'cancel';
export type CommerceShipmentStatus = CommerceStatus;

/**
 * Vendor-facing shipment, standalone (returned by the vendor shipments-list endpoint, not
 * nested in an order) — carries the parent order's delivery context directly since a vendor
 * only ever sees its own shipment, never the whole multi-vendor order.
 */
export type CommerceShipment = {
  id: string;
  order_id: string;
  vertical: CommerceVertical;
  status: CommerceStatus;
  subtotal_inr: string;
  tracking_ref: string | null;
  tracking_url: string | null;
  cancelled_by: string | null;
  cancel_reason: string | null;
  contact_phone: string;
  address: string | null;
  landmark: string | null;
  city: string | null;
  pincode: string | null;
  notes: string | null;
  prescription_required: boolean;
  prescriptions: CatalogImage[];
  created_at: string;
  lines: CommerceOrderLine[];
};

export type SetShipmentTrackingInput = { tracking_ref?: string; tracking_url?: string };

// ---- Owner: place order ----
export type PlaceCommerceOrderItemInput = {
  vertical: CommerceVertical;
  vendor_id: string;
  variant_id: string;
  quantity: number;
};

export type PlaceCommerceOrderInput = {
  contact_phone: string;
  /** Pick a saved address (dereferenced server-side) instead of the inline fields. */
  saved_address_id?: string;
  address?: string;
  landmark?: string;
  city?: string;
  pincode?: string;
  notes?: string;
  /**
   * Omit to order the server-side cart (the normal path). Only sent for a direct
   * "buy now" that must not touch the cart.
   */
  items?: PlaceCommerceOrderItemInput[];
  /**
   * The grand total this client is showing on its Pay button. The server refuses the order
   * with `422 PRICE_CHANGED` if its own arithmetic disagrees, so a vendor re-pricing an item
   * mid-cart can't charge an amount the shopper never saw. Always send it.
   */
  expected_grand_total_inr?: number;
};

// ---- Food catalog ----
export type FoodProductCategory = 'DRY_FOOD' | 'WET_FOOD' | 'TREATS' | 'SUPPLEMENT' | 'TOYS' | 'OTHER';

export const FOOD_CATEGORY_OPTIONS: { value: FoodProductCategory; label: string }[] = [
  { value: 'DRY_FOOD', label: 'Dry food' },
  { value: 'WET_FOOD', label: 'Wet food' },
  { value: 'TREATS', label: 'Treats' },
  { value: 'SUPPLEMENT', label: 'Supplement' },
  { value: 'TOYS', label: 'Toys' },
  { value: 'OTHER', label: 'Other' },
];

/**
 * Where a listing stands with the reviewer, and what customers will pay.
 *
 * Both are new: products now need admin approval before shoppers see them, and the
 * platform fee is added on top of the vendor's price. A vendor shown only their own
 * number would not know either.
 */
export type ProductApprovalFields = {
  approval_status: 'PENDING' | 'APPROVED' | 'REJECTED';
  /** The reviewer's reason, when they refused it. */
  approval_note: string | null;
  reviewed_at: string | null;
  /** True when an edit is queued; the previously approved version is still live. */
  has_pending_changes: boolean;
  is_visible_to_customers: boolean;
  /** One line, already worded for display. */
  status_label: string;
};

export type VariantPricingDto = {
  variant_id: string;
  vendor_price_inr: string;
  mrp_inr: string | null;
  platform_fee_inr: string;
  customer_price_inr: string;
  commission_mode: 'RATE' | 'FLAT';
  commission_rate_pct: number | null;
  commission_source: 'VARIANT' | 'PRODUCT' | 'CATEGORY' | 'NONE';
  exceeds_mrp: boolean;
};

/** A product the platform curates once, that many vendors can link to. */
export type CatalogProduct = {
  id: string;
  vertical: 'FOOD' | 'PHARMACY';
  name: string;
  brand: string | null;
  category: string;
  pack_size: string | null;
  description: string | null;
  is_prescription_required: boolean;
  /** The curated gallery, in order — what a product page leads with. */
  images?: { id: string; path: string }[];
  image_path: string | null;
  is_active: boolean;
};

export type FoodProduct = ProductApprovalFields & {
  id: string;
  name: string;
  description: string | null;
  brand: string | null;
  pack_size: string | null;
  price_inr: string | null; // legacy base price — variants are authoritative
  category: FoodProductCategory;
  is_available: boolean;
  display_order: number;
  /** Which pets it is for; empty = all pets (client change, 9 Oct 2026). */
  species?: PetSpecies[];
  variants: ProductVariant[];
  images: CatalogImage[];
  /** Per variant: what the vendor gets, the fee, and what the customer pays. */
  pricing?: VariantPricingDto[];
  /** Set when this listing came from the curated catalogue rather than typed. */
  catalog_product_id: string | null;
  is_from_catalog: boolean;
  /** The master's photo, when linked — a linked listing needs no upload. */
  catalog_image_path: string | null;
  /** Every curated photo, in order — what shoppers see before the vendor's own. */
  catalog_images?: { id: string; path: string }[];
  created_at: string;
  updated_at: string;
};

export type CreateFoodProductInput = {
  name: string;
  description?: string;
  brand?: string;
  pack_size?: string;
  category: FoodProductCategory;
  is_available?: boolean;
  display_order?: number;
  /** Empty = all pets. Not reviewed: applies at once even on an approved product. */
  species?: PetSpecies[];
  variants: CreateVariantInput[]; // ≥1 required — price lives on variants
};

/**
 * Listing a curated master instead of typing a product out.
 *
 * A separate input rather than an optional field on CreateFoodProductInput because
 * the two are mutually exclusive: the master owns the identity, so sending a name
 * alongside a link is refused by the API. The vendor supplies only the commercial
 * half — which sizes they stock, at what price.
 */
export type LinkFoodCatalogProductInput = {
  catalog_product_id: string;
  variants: CreateVariantInput[];
  is_available?: boolean;
};

/** Product-field updates only; variants are managed through the variant endpoints. */
export type UpdateFoodProductInput = Partial<Omit<CreateFoodProductInput, 'variants'>>;

// ---- Food onboarding module details ----
export type FoodDetailsRequest = { fssai_license_number?: string };

export type FoodDetailsResponse = {
  id: string;
  vendor_profile_id: string;
  fssai_license_number: string | null;
  created_at: string;
  updated_at: string;
};
